import { useState, forwardRef, type CSSProperties, type HTMLAttributes } from 'react';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { ReorderItem } from './imageSlotReorder';

type Props = {
  /** いまの並び (位置 i = 枠 i) */
  items: ReorderItem[];
  /** 位置 i の枠ラベル (メイン画像 / 画像 1 ...) */
  labelAt: (index: number) => string;
  onChange: (items: ReorderItem[]) => void;
  /** 送信中など、操作を受け付けないとき */
  disabled?: boolean;
  className?: string;
};

/**
 * 商品画像枠の並べ替えグリッド (Amazon / マニュアル共通)。
 * カード全体をつかんで動かすと他のカードが滑らかに詰まり (dnd-kit sortable)、
 * 空き枠も位置として扱う (空き枠へ動かせる・空き枠自体はつかめない)。
 * 並びの確定 (保存・送信) は呼び出し側が「完了」で行う。
 */
export function ImageSlotReorderGrid({ items, labelAt, onChange, disabled, className }: Props) {
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const sensors = useSensors(
    // クリックと区別するため、数 px 動いてからドラッグ開始にする
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragStart = (e: DragStartEvent) => setActiveKey(String(e.active.id));
  const handleDragEnd = (e: DragEndEvent) => {
    setActiveKey(null);
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const from = items.findIndex((item) => item.key === active.id);
    const to = items.findIndex((item) => item.key === over.id);
    if (from < 0 || to < 0) return;
    onChange(arrayMove(items, from, to));
  };

  const activeIndex = activeKey ? items.findIndex((item) => item.key === activeKey) : -1;
  const activeItem = activeIndex >= 0 ? items[activeIndex] : null;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={() => setActiveKey(null)}
    >
      <SortableContext items={items.map((item) => item.key)} strategy={rectSortingStrategy} disabled={disabled}>
        <div className={`additional-slots-grid reorder-grid${className ? ` ${className}` : ''}`}>
          {items.map((item, index) => (
            <SortableSlotCard key={item.key} item={item} label={labelAt(index)} disabled={disabled} />
          ))}
        </div>
      </SortableContext>
      {/* つかんだカードは少し浮かせて表示し、元の位置は薄く残す */}
      <DragOverlay dropAnimation={{ duration: 180, easing: 'cubic-bezier(0.2, 0, 0, 1)' }}>
        {activeItem ? (
          <SlotCardBody item={activeItem} label={labelAt(activeIndex)} className="image-slot-card reorder-card reorder-card--lifted" />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

function SortableSlotCard({ item, label, disabled }: { item: ReorderItem; label: string; disabled?: boolean }) {
  const canDrag = !!item.url && !disabled;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.key,
    // 空き枠はつかめないが、他のカードを落とす位置としては使う
    disabled: { draggable: !canDrag, droppable: !!disabled },
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  return (
    <SlotCardBody
      ref={setNodeRef}
      item={item}
      label={label}
      style={style}
      className={`image-slot-card reorder-card${canDrag ? ' reorder-card--grabbable' : ''}${isDragging ? ' reorder-card--placeholder' : ''}`}
      {...attributes}
      {...listeners}
    />
  );
}


type BodyProps = HTMLAttributes<HTMLDivElement> & {
  item: ReorderItem;
  label: string;
  style?: CSSProperties;
};

const SlotCardBody = forwardRef<HTMLDivElement, BodyProps>(function SlotCardBody({ item, label, className, ...rest }, ref) {
  return (
    <div ref={ref} className={className} {...rest}>
      <div className="slot-header">
        <div className="slot-label" title={item.name || undefined}>{label}</div>
        {item.name && <div className="reorder-card-name" title={item.name}>{item.name}</div>}
      </div>
      <div className="slot-preview">
        {item.url ? (
          <img src={item.url} alt={label} draggable={false} />
        ) : (
          <div className="empty-slot reorder-empty">
            <span className="empty-text">空き</span>
          </div>
        )}
      </div>
    </div>
  );
});
